import * as React from "react";

import * as R from "ramda";

import classNames from "classnames";

import DocActivity from "./Activity";
import DocAudio from "./Audio";
import DocButton from "./Button";
import DocCheckbox from "./Checkbox";
import DocCompletionNotice from "./CompletionNotice";
import DocExtra from "./Extra";
import DocImage from "./Image";
import DocImageGallery from "./ImageGallery";
import DocMatcher from "./Matcher";
import DocMultipleChoice from "./MultipleChoice";
import DocNumberedList from "./NumberedList";
import DocOpenAiChat from "./OpenAiChat";
import DocParagraph from "./Paragraph";
import DocRatingBar from "./RatingBar";
import DocRegularList from "./RegularList";
import DocSliderBar from "./SliderBar";
import DocTextInput from "./TextInput";
import DocVideo from "./Video";

import GenSwitch, {switchModes} from "../Gen/Switch";

import DocInlineText from "./InlineText";

import AddToButton from "../DocumentSetDocumentRelation/AddTo/Button";

import { documentDisplayNumber } from "../../src/document_display_title";

import { hideUntilShouldHideNow } from "../../src/effective_dates";

import { isDrawerUid } from "../../src/drawers";

interface RootDocSectionTrue {
  isRootSection: true;
  currentGroup: CurrentGroup;
}

interface RootDocSectionFalse {
  isRootSection?: false;
}

type RootDocSection = RootDocSectionTrue | RootDocSectionFalse


interface DocSectionProps extends SectionNode, EveryNodeArgs {
  aboveContent?: React.ReactNode;
  currentGroup?: CurrentGroup;
  headerColor?: string;
  imagingTypes?: Array<ImagingType>;
  reportButton?: React.ReactNode;
  rightContent?: React.ReactNode;
  titleBadge?: React.ReactNode;
  currentActivityCustomEntries?: Record<string, string>;
}

interface DocSectionState {

}

export default class DocSection extends React.Component<DocSectionProps & RootDocSection, DocSectionState> {
  state = {
    switchMode: null,
  };

  ref = React.createRef<HTMLDivElement>();

  onHeaderClick() {
    const {
      collapsed,
      collapsible,
      inactiveBoolean,
      nested,
      uid,

      currentUser,
      displayState,
    } = this.props;

    if (inactiveBoolean) { return; }

    if (!nested) {
      displayState.openSection(uid);
    } else if(collapsible) {
      if (collapsed) {
        currentUser.logEvent(uid, "section_uncollapsed");
      }

      currentUser.addState({[uid]: {"collapsed": !collapsed}});
    }
  }

  getSnapshotBeforeUpdate(prevProps: DocSectionProps, prevState: DocSectionState): DOMRect | null {
    if (prevProps.collapsed !== this.props.collapsed) {
      return this.ref.current?.getBoundingClientRect() || null;
    }

    return null;
  }

  componentDidUpdate(prevProps: DocSectionProps, prevState: DocSectionState, snapshot: DOMRect | null) {
    if (snapshot && prevProps.collapsed !== this.props.collapsed) {
      const prevBoundingClientRect = snapshot;
      const currBoundingClientRect = this.ref.current?.getBoundingClientRect();
      if (this.props.collapsed && currBoundingClientRect) {
        if (prevBoundingClientRect.bottom < 0) {
          window.scrollBy(0, currBoundingClientRect.height - prevBoundingClientRect.height);
        } else if (prevBoundingClientRect.top < 0) {
          window.scrollBy(0, currBoundingClientRect.top);
        }
      }
    }
  }

  render() {
    const {
      aboveContent,
      classes,
      collapsed,
      collapsible,
      content,
      headerColor,
      inactive,
      inactiveBoolean,
      isRootSection,
      nested,
      rightContent,
      title,
      titleBadge,
      uid,
      reportButton,

      currentDocument,
      currentDocumentSetDocumentRelation,
      currentGroup,
      currentRoleDefinition,
      currentUser,
      currentVersion,
      displayState,
      editState,

      isGated,

      currentActivityUserState,
      currentActivityCustomEntries,

      activityActions,
    } = this.props;

    const htmlClass = classNames(
      "doc-section",
      {
        "nested": nested,
        "collapsible": collapsible,
        "collapsed": collapsed,
        "inactive": inactiveBoolean,
      },
    );

    let hideBadge, scheduleToggle;
    if (currentDocumentSetDocumentRelation && isDrawerUid(uid)) {
      const shouldHide =
        currentDocumentSetDocumentRelation &&
        currentDocumentSetDocumentRelation.drawer_schedules[uid] &&
        hideUntilShouldHideNow(currentDocumentSetDocumentRelation.drawer_schedules[uid].hide_until)

      if (currentRoleDefinition.isLearner || isGated) {
        if (shouldHide) { return null; }
      } else {
        hideBadge = (
          <GenSwitch
            value={shouldHide ? switchModes.OFF : switchModes.ON}
            onClick={(newSwitchMode) => {
              switch (newSwitchMode) {
                case switchModes.ON:
                  currentDocumentSetDocumentRelation._actions.setEditFields({drawer_schedules: {
                    ...currentDocumentSetDocumentRelation.drawer_schedules,
                    [uid]: {hide_until: false},
                  }});
                  break;
                case switchModes.OFF:
                  currentDocumentSetDocumentRelation._actions.setEditFields({drawer_schedules: {
                    ...currentDocumentSetDocumentRelation.drawer_schedules,
                    [uid]: {hide_until: true},
                  }});
                  break;
              }

              currentDocumentSetDocumentRelation._actions.update();
            }}
          />
        );
      }
    }

    const headerClasses = classNames(
      "doc-section-header",
      {"display-root": isRootSection},
    );

    const mediaIcons: Array<React.ReactNode> = [];
    if (classes) {
      classes.forEach((childClass) => {
        switch (childClass) {
          case "extra_video":
            mediaIcons.push(<i key="extra_video" className="fa fa-video" style={{"paddingLeft": "5px"}} />);
            break;
          case "extra_image":
            mediaIcons.push(<i key="extra_image" className="fa fa-image" style={{"paddingLeft": "5px"}} />);
            break;
          case "extra_audio":
            mediaIcons.push(<i key="extra_audio" className="fa fa-volume-high" style={{"paddingLeft": "5px"}} />);
            break;
          default:
        }
      });
    }

    let coverImage;
    if (isRootSection && editState.isEditor && editState.isEditing && displayState.documentType !== "Activity" && "aws_folder_url" in currentVersion) {
      coverImage = (
        <img
          className="doc-section-header-cover-image"
          src={`${currentVersion.aws_folder_url}/compressed/images/${currentVersion.cover_image}`}
          onClick={(a) => displayState.setOpenMenu({name: "MEDIA_MENU", mediaType: "image", uid: "cover_image"})}
        />
      );
    }

    let collapsedIndicator;
    let readerTags = {};
    if (collapsible) {
      readerTags = {
        role: "button",
        "aria-label": `Toggle ${title}`,
        "aria-expanded": !collapsed,
        "aria-disabled": inactive,
      };

      collapsedIndicator = (
        <span className="doc-section-collapsed-indicator">
          <i className={`fa fa-caret-${collapsed ? "right" : "down"}`} />
        </span>
      );
    }

    let addToButton;
    if (isRootSection && !editState.isEditor && currentDocument.type !== "Activity" && currentRoleDefinition.name !== "Learner" && !currentDocumentSetDocumentRelation) {
      addToButton = (
        <AddToButton
          document={currentDocument as DocumentSnakeModel} // TODO: Remove as
          currentUser={currentUser}
          currentRoleDefinition={currentRoleDefinition}
        />
      );
    }

    let resetButton;
    if (isRootSection && !editState.isEditor && (currentUser?.is_admin || ["Faculty", "Manager", "Partner", "ReelDx_Faculty"].includes(currentRoleDefinition?.name) || currentGroup?.subdomain === "reeldxinterns")) {
      resetButton = (
        <a
          className="bg-sky-600 hover:bg-sky-500 button color-white no-underline ml-3 text-nowrap"
          style={{fontSize: "1em", padding: ".7em 1em", lineHeight: "1"}}
          rel="nofollow"
          data-turbo-method="delete"
          data-turbo-confirm="Are you sure you want to reset your case?"
          href={
          currentDocumentSetDocumentRelation ?
            `/document_set_document_relations/${currentDocumentSetDocumentRelation.id}/destroy_progress?user=${currentUser.id}`
            : `/documents/${currentDocument.id}/usc_delete?user=${currentUser.id}`
        }
        >
          Reset Case
        </a>
      );
    }

    const sectionHeader = (
      <div className={headerClasses} onClick={this.onHeaderClick.bind(this)} {...readerTags} style={headerColor ? {"backgroundColor": headerColor} : {}}>
        <h1 className="doc-section-header-title">
          {collapsedIndicator}
          {coverImage}
          {isRootSection && currentDocument.type !== "Activity" && `${documentDisplayNumber(currentDocument as DocumentSnakeModel)} | `}
          <DocInlineText content={title} uid={uid} contentKey="title" editState={editState}/>
          {mediaIcons}
        </h1>
        {inactiveBoolean && (
          <div className="skill-badge bg-sky-600 mr-2">
            <i className="fa fa-lock" />
          </div>
        )}
        {titleBadge && (
          <div className="doc-section-header-title-badge">
            {titleBadge}
          </div>
        )}
        {(rightContent || hideBadge || addToButton || resetButton) && (
          <div className="doc-section-right-content">
            {addToButton}
            {resetButton}
            {rightContent}
            {scheduleToggle}
            {hideBadge}
          </div>
        )}
      </div>
    );

    let visibleContent: Array<BlockContent>, renderedAboveContent, renderedReportButton;
    if (!displayState.isForcedUncollapsed && ((!isRootSection && !nested) || collapsed || inactiveBoolean)) {
      visibleContent = [];
    } else {
      if (reportButton) {
        renderedReportButton = reportButton;
      }

      visibleContent = content.filter((node) => node.invisible !== true);

      if (aboveContent) {
        renderedAboveContent = (
          <div className="doc-section-body-above-content">
            {aboveContent}
          </div>
        );
      }
    }

    const passToEveryNode = {
      currentDocument: currentDocument,
      currentVersion: currentVersion,
      currentUser: currentUser,
      displayState: displayState,
      editState: editState,

      currentDocumentSetDocumentRelation: currentDocumentSetDocumentRelation,
      currentRoleDefinition: currentRoleDefinition,

      currentActivityUserState: currentActivityUserState,
      currentActivityCustomEntries,

      isGated,

      activityActions,
    };

    const contentNodes = visibleContent.map((node, index) => {
      if (node.hasOwnProperty("inactive") && node.inactive !== 0) {
        node.inactiveBoolean = node.inactive === 2;
      } else {
        node.inactiveBoolean = inactiveBoolean;
      }

      if (R.includes(node.type, ["section", "image", "video"]) && index > 0) {
        (node as any).imagingTypes = this.props.imagingTypes; // TODO: FIX
      }

      const nodeContent = {
        key: node.uid,
        ...node,
        ...passToEveryNode,
      };

      switch(nodeContent.type){
        case "section":
          return <DocSection {...nodeContent} />;
        case "paragraph":
          return <DocParagraph {...nodeContent} />;
        case "regularList":
          return <DocRegularList {...nodeContent} />;
        case "numberedList":
          return <DocNumberedList {...nodeContent} />;
        case "extra":
          return <DocExtra {...nodeContent} />;
        case "image":
          return <DocImage {...nodeContent} />;
        case "imageGallery":
          return <DocImageGallery {...nodeContent} />;
        case "video":
          return <DocVideo {...nodeContent} />;
        case "audio":
          return <DocAudio {...nodeContent} />;
        case "checkbox":
          return <DocCheckbox {...nodeContent} />;
        case "textInput":
          return <DocTextInput {...nodeContent} />;
        case "sliderBar":
          return <DocSliderBar {...nodeContent} />;
        case "ratingBar":
          return <DocRatingBar {...nodeContent} />;
        case "multipleChoice":
          return <DocMultipleChoice {...nodeContent} />;
        case "matcher":
          return <DocMatcher {...nodeContent} />;
        case "button":
          return <DocButton {...nodeContent} />;
        case "activity":
          return <DocActivity {...nodeContent} />;
        case "openAiChat":
          return <DocOpenAiChat {...nodeContent} />;
        case "completionNotice":
          return <DocCompletionNotice {...nodeContent} />;
        default:
          // TODO: ADD WARNING IF IN DISPLAY MORE
          return null;
      }
    });

    const sectionBody = (
      <div className="doc-section-body relative">
        {renderedAboveContent}
        {contentNodes}
        {renderedReportButton}
      </div>
    );

    return (
      <div ref={this.ref} className={htmlClass}>
        {sectionHeader}
        {/* {isRootSection && <DocCompletionNotice {...passToEveryNode} />} */}
        {sectionBody}
      </div>
    );
  }
}
